import { Acabados, Item, Node, Textures, Zone } from '@types'
import getStandardMaterial from '@utils/conversion/getStandardMaterial'
import { scheduleInvalidateR3F } from '@utils/invalidateR3F'
import { clampTextureImageToGpuBudget } from '@utils/sceneObjects/clampGpuTexture'
import {
  applyCatalogSwatchUvsIfNeeded,
  isolateGeometryUserData,
  zoneNamesMatch,
} from '@utils/sceneObjects/applyCatalogSwatchUvs'
import { GLB_INSTANCE_OWNED_GEOMETRY } from '@utils/sceneObjects/disposeGlbClone'
import {
  loadCachedTexture,
  normalizeTextureUrl,
  setMaterialMap,
  setMaterialMapUnique,
} from '@utils/sceneObjects/loadedTextureCache'
import { tuneMaterialForAlzadoTexturedView } from '@utils/sceneObjects/alzadoMaterialTuning'
import { isFrontalZoneTexture } from '@utils/sceneObjects/frontals/frontalRuns'
import { getLinkedZoneVisualKey } from '@utils/sceneObjects/linked/linkedTextureFields'
import {
  findTextureForMeshNode,
  isLinkedFurnitureFinishTexture,
  isLinkedPanelFinishTexture,
  collectValidZonesFromUnitAcabados,
} from '@utils/sceneObjects/linked/linkedZoneTextureMapping'
import { radians } from '@utils/generalMaths/geometry'
import {
  ENCIMERA_ATLAS_HEIGHT_MM,
  ENCIMERA_ATLAS_WIDTH_MM,
  getEncimeraUvPlacement,
} from '@utils/sceneObjects/encimeraTextureLayout'
import {
  isDimFlowEnabled,
  logDimFlow,
} from '@utils/debug/dimensionFlowDiagnostic'
import * as THREE from 'three'
import { ClampToEdgeWrapping, Group, Mesh, RepeatWrapping, TextureLoader } from 'three'

const dimFlowMorphKeys = new Set<string>()

// Cargador legacy (ventanas); unidades linked usan loadCachedTexture.
const loader = new TextureLoader()

function assignNodeTexture(
  node: Node & { material: THREE.Material },
  zoneTexture: Textures[number],
  zones: Zone,
  mapTex: THREE.Texture,
  baseTex?: THREE.Texture,
) {
  if (baseTex && mapTex !== baseTex) {
    setMaterialMapUnique(node.material, mapTex, baseTex)
  } else {
    setMaterialMap(node.material, mapTex)
  }
  node.material.userData.currentTexture = zoneTexture.url
  node.material.userData.currentVisualKey = getLinkedZoneVisualKey(zoneTexture)
  applyZoneMaterialFromZones(node, zones, zoneTexture)
  tuneMaterialForAlzadoTexturedView(
    node.material,
    zones?.[node.name]?.material?.roughness as number | undefined,
  )
}

/** Atlas encimera estándar (mm); repeat proporcional al tamaño del hijo, sin offset por contiguos. */
const ENCIMERA_TEXTURE_REF_WIDTH_MM = 3200
const ENCIMERA_TEXTURE_REF_HEIGHT_MM = 1200

/**
 * UV repeat encimera según ancho/fondo reales (LB8060, RB805, laterales free-dim…).
 * Compensa scale del grupo padre (mismo criterio que zócalo en ObjectMeshENC).
 */
export function applyEncimeraTextureRepeat(
  tex: THREE.Texture,
  itemWidth: number,
  itemThickness: number,
  repeatXStore: number,
  repeatYStore: number,
  textureRotation: number,
  freeDimensionTextureScale?: { x: number; y: number; z: number },
): void {
  const compX = freeDimensionTextureScale?.x ?? 1
  const compZ = freeDimensionTextureScale?.z ?? 1
  const repeatXCalculated =
    ((itemWidth / ENCIMERA_TEXTURE_REF_WIDTH_MM) * (repeatXStore ?? 1)) / (compX > 0 ? compX : 1)
  const repeatYCalculated =
    ((itemThickness / ENCIMERA_TEXTURE_REF_HEIGHT_MM) * (repeatYStore ?? 1)) /
    (compZ > 0 ? compZ : 1)
  tex.offset.set(0, 0)
  tex.rotation = radians(textureRotation)
  tex.repeat.set(repeatXCalculated, repeatYCalculated)
  tex.wrapS = RepeatWrapping
  tex.wrapT = RepeatWrapping
  tex.flipY = false
  tex.needsUpdate = true
}

const FRONTAL_TEXTURE_REF_WIDTH_MM = 3200
const FRONTAL_TEXTURE_REF_HEIGHT_MM = 1200

/** Repeat de la plancha 3200×1200 del frontal según ancho/alto reales (mm). */
export function applyFrontalSlabTextureRepeat(
  tex: THREE.Texture,
  itemWidth: number,
  itemHeight: number,
  textureRotation: number,
): void {
  const widthMm = itemWidth > 0 ? itemWidth : FRONTAL_TEXTURE_REF_WIDTH_MM
  const heightMm = itemHeight > 0 ? itemHeight : FRONTAL_TEXTURE_REF_HEIGHT_MM
  tex.offset.set(0, 0)
  tex.rotation = radians(textureRotation)
  tex.repeat.set(
    widthMm / FRONTAL_TEXTURE_REF_WIDTH_MM,
    heightMm / FRONTAL_TEXTURE_REF_HEIGHT_MM,
  )
  tex.wrapS = RepeatWrapping
  tex.wrapT = RepeatWrapping
  tex.flipY = false
  tex.needsUpdate = true
}

function isNumericCadZoneName(value: unknown): boolean {
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  if (!trimmed) return false
  return /^\d+$/.test(trimmed)
}

function textureTargetsNode(
  texture: Textures[number],
  nodeName: string,
  textures: Textures,
  zones: Zone,
): boolean {
  if (zoneNamesMatch(texture.zoneName, nodeName) || texture.zoneName === nodeName) return true
  const resolved = findTextureForMeshNode(nodeName, zones, textures)
  if (resolved) return resolved === texture
  if (!isFrontalZoneTexture(texture)) return false
  return !textures.some(
    (other) =>
      other !== texture &&
      (zoneNamesMatch(other.zoneName, nodeName) || other.zoneName === nodeName),
  )
}

/** Intensidad mínima para zonas Luz (tiras LED, etc.) — visible en visor y en export HD. */
const LUZ_ZONE_EMISSIVE_INTENSITY = 2

export function isLuzZone(texture?: { zone?: string; zoneDisplayName?: string }) {
  return texture?.zone === 'Luz' || texture?.zoneDisplayName === 'Luz'
}

export function applyLuzZoneEmissive(node: Node & { material?: THREE.Material }) {
  const mat = node.material as THREE.MeshStandardMaterial | undefined
  if (!mat || !('emissive' in mat)) return
  mat.userData.isLuzZone = true
  if (mat.emissive) {
    mat.emissive.set(mat.color ?? '#ffffff')
  }
  if ('emissiveIntensity' in mat) {
    mat.emissiveIntensity = Math.max(mat.emissiveIntensity ?? 0, LUZ_ZONE_EMISSIVE_INTENSITY)
  }
  if (mat.map && 'emissiveMap' in mat) {
    mat.emissiveMap = mat.map
  }
  if ('toneMapped' in mat) {
    mat.toneMapped = false
  }
  mat.needsUpdate = true
}

export function applyZoneMaterialFromZones(
  node: Node & { name: keyof Zone },
  zones?: Zone,
  zoneTexture?: { zone?: string; zoneDisplayName?: string; modelo?: string },
) {
  const zoneMaterial =
    zones?.[node.name]?.material ??
    Object.values(zones ?? {}).find((zone) => zoneNamesMatch(zone?.name, String(node.name)))
      ?.material ??
    (zoneTexture
      ? Object.values(zones ?? {}).find(
          (zone) =>
            zone?.material?.zoneDisplayName === zoneTexture.zoneDisplayName ||
            zone?.material?.zone === zoneTexture.zoneDisplayName ||
            zone?.material?.zone === zoneTexture.zone,
        )?.material
      : undefined) ??
    (isFrontalZoneTexture(zoneTexture)
      ? Object.values(zones ?? {}).find((zone) => isFrontalZoneTexture(zone?.material))?.material
      : undefined)
  if (!zoneMaterial || !node.material) return
  const luzZone = isLuzZone(zoneTexture)
  if (zoneMaterial.roughness !== undefined) {
    node.material.roughness = zoneMaterial.roughness
  }
  if (zoneMaterial.metalness !== undefined) {
    node.material.metalness = zoneMaterial.metalness
  }
  if (zoneMaterial.envMapIntensity !== undefined) {
    node.material.envMapIntensity = zoneMaterial.envMapIntensity
  }
  if (zoneMaterial.bumpScale !== undefined) {
    node.material.bumpScale = zoneMaterial.bumpScale
  }
  if (zoneMaterial.displacementScale !== undefined) {
    node.material.displacementScale = zoneMaterial.displacementScale
  }
  if (zoneMaterial.opacity !== undefined) {
    node.material.opacity = zoneMaterial.opacity
    node.material.transparent = zoneMaterial.opacity < 1
    node.material.depthWrite = zoneMaterial.opacity >= 1
  }
  if (zoneMaterial.color !== undefined) {
    node.material.color.set(zoneMaterial.color)
  }
  if (luzZone) {
    applyLuzZoneEmissive(node)
  } else {
    if ('emissive' in node.material && node.material.emissive) {
      node.material.emissive.setHex(0x000000)
    }
    if ('emissiveIntensity' in node.material) {
      node.material.emissiveIntensity = 0
    }
    if (node.material.userData) {
      delete node.material.userData.isLuzZone
    }
  }
  node.material.needsUpdate = true
}

interface ContiguousFurnitureItem {
  id: string
  catalogID: string
  position: { x: number; y: number; z: number }
  width: number
  height: number
  thickness: number
  rotation: number
  orderIndex: number
  type: string
  description: string
  /**
   * Topología del grupo conexo al que pertenece la fila (la rellena getContItems desde
   * snappedSides). Es lo que permite saber que dos filas son la misma isla y deben
   * compartir una única plancha de textura. Opcional: las entradas de proyectos que aún
   * no la traen caen en el modo fila de siempre.
   */
  groupId?: string | number
  groupShape?: string
  rowIndex?: number
  rowCount?: number
}
// Interfaz para el resultado del análisis de muebles contiguos
interface ContiguousAnalysisResult {
  groups: ContiguousFurnitureItem[][]
  totalGroups: number
  totalItemsInGroups: number
}
// Función exportada para llamar desde componentes con acceso al store
export function analyzeContiguousFurniture(getContiguousItems: () => ContiguousAnalysisResult) {
  try {
    const result = getContiguousItems()
    return result
  } catch (error) {
    return null
  }
}
/**
 * Morph target "Regleta": GLB en reposo = MÁXIMO (p.ej. 120).
 * influence 0 = max; influence 1 = min → sidebar 20 aplica 1.
 */
export function getRegletaMorphInfluence(regletaMorph: {
  default?: number
  min?: number
  max?: number
}): number {
  const min = Number(regletaMorph.min)
  const max = Number(regletaMorph.max)
  const raw = Number(regletaMorph.default)
  if (!(max > min)) return 0
  let t = (raw - min) / (max - min)
  if (raw > max) t = 1
  else if (raw < min) t = 0
  t = Math.max(0, Math.min(1, t))
  return 1 - t
}

/** Claves del morphTargetDictionary que corresponden a Regleta (el nombre GLB puede variar). */
export function findRegletaMorphDictKeys(
  dict: Record<string, number> | undefined | null,
): string[] {
  if (!dict) return []
  return Object.keys(dict).filter((k) => k === 'Regleta' || /regleta/i.test(k))
}

function morphNameMatchesProperty(dictKey: string, propertyName: string): boolean {
  if (dictKey === propertyName) return true
  if (dictKey.toLowerCase() === propertyName.toLowerCase()) return true
  return propertyName === 'Regleta' && /regleta/i.test(dictKey)
}

export function handleNodeMorphs(node: Node, textures: Item['morphs']) {
  if (node.morphTargetDictionary) {
    const applied: Array<{ morph: string; influence: number; defaultMm?: number }> = []
    Object.keys(node.morphTargetDictionary).forEach((morph) => {
      // Usar el índice del diccionario (no el de Object.keys): si no coinciden, Alto/Ancho se cruzan.
      const morphIndex = node.morphTargetDictionary![morph]
      if (morphIndex == null || !node.morphTargetInfluences) return
      textures.forEach((property) => {
        if (morphNameMatchesProperty(morph, property.name)) {
          if (property.max !== property.min) {
            let value: number

            // Si el valor está dentro de los límites, calcular normalmente
            if (property.default >= property.min && property.default <= property.max) {
              value = (property.default - property.min) / (property.max - property.min)
            }
            // Si el valor supera el máximo, aplicar al máximo (1.0) - el CORTEKIT se encargará del resto
            else if (property.default > property.max) {
              value = 1.0
            }
            // Si el valor es menor que el mínimo, aplicar al mínimo (0.0)
            else {
              value = 0.0
            }

            let influence: number
            if (property.name === 'Regleta') {
              influence = getRegletaMorphInfluence(property)
            } else if (property.reversed) {
              influence = 1 - value
            } else {
              influence = value
            }
            node.morphTargetInfluences![morphIndex] = influence
            applied.push({
              morph,
              influence,
              defaultMm: property.default,
            })
          } else if (node.morphTargetInfluences) {
            // min===max (free-dim escala grupo): forzar influence 0 para no dejar morphs viejos.
            node.morphTargetInfluences[morphIndex] = 0
          }
        }
      })
    })
    // Log escaso: deduplicar por mesh+influences para no saturar consola
    if (applied.length > 0 && (node as { name?: string }).name && isDimFlowEnabled()) {
      const key = `${(node as { name?: string }).name}|${applied.map((a) => `${a.morph}:${a.influence.toFixed(3)}`).join(',')}`
      if (!dimFlowMorphKeys.has(key)) {
        dimFlowMorphKeys.add(key)
        if (dimFlowMorphKeys.size > 200) dimFlowMorphKeys.clear()
        logDimFlow({
          file: 'utils/sceneObjects/getZonesFromGLB.tsx',
          fn: 'handleNodeMorphs',
          phase: 'morphs.apply',
          reason:
            'Aplicación morphTargetInfluences en malla. influence = (default−min)/(max−min) clamped 0..1. Esto deforma la geometría real del GLB.',
          snapshot: {
            extra: {
              meshName: (node as { name?: string }).name,
              applied,
            },
            usesMorph: true,
            morphsActive: applied.map((a) => ({
              name: a.morph,
              default: a.defaultMm,
            })),
          },
        })
      }
    }
  }
  // Regleta en mallas con morph target Regleta (Mesh004, etc.).
  const regletaMorph = textures?.find((morph) => morph.name === 'Regleta')
  const regletaKeys = findRegletaMorphDictKeys(node.morphTargetDictionary)
  if (regletaMorph && node.morphTargetInfluences && regletaKeys.length > 0) {
    const influence = getRegletaMorphInfluence(regletaMorph)
    for (const key of regletaKeys) {
      const morphIndex = node.morphTargetDictionary![key]
      if (morphIndex !== undefined) {
        node.morphTargetInfluences[morphIndex] = influence
      }
    }
  }
}
function findMatchingAcabado(materialName: string, acabados: Acabados[]): Acabados | undefined {
  if (!materialName) return undefined

  const exact = acabados.find((acabado) => acabado.zona === materialName)
  if (exact) return exact

  const cleanMaterialName = materialName.replace(/[0-9]/g, '').trim()
  if (cleanMaterialName.length < 4) return undefined

  const hasCostadoAcabado = acabados.some(
    (acabado) => acabado.zona === 'Costado' || acabado.descripcion === 'Costado',
  )
  if (!hasCostadoAcabado && /^costado$/i.test(cleanMaterialName)) {
    return undefined
  }

  return acabados.find((acabado) => {
    const cleanZonaName = acabado.zona.replace(/[0-9]/g, '').trim()
    if (cleanZonaName.length < 4) return false
    return (
      cleanMaterialName === cleanZonaName ||
      cleanMaterialName.includes(cleanZonaName) ||
      cleanZonaName.includes(cleanMaterialName)
    )
  })
}

/** idAcabadoUnidad para el configurador (Encimera B80new vs B10 en el GLB). */
function resolveLinkedTextureIdForAcabado(
  acabado: Acabados,
  extraInfo?: { zonas: { idAcabadoUnidad: number; zona: string }[] },
): number | undefined {
  const zonas = extraInfo?.zonas
  if (!zonas?.length) {
    return acabado.idAcabadoUnidad
  }
  const exact = zonas.find((z) => z.zona === acabado.descripcion || z.zona === acabado.zona)
  if (exact?.idAcabadoUnidad && exact.idAcabadoUnidad > 0) {
    return exact.idAcabadoUnidad
  }
  const isEncimera =
    /encimera/i.test(acabado.zona) || /encimera/i.test(acabado.descripcion ?? '')
  if (isEncimera) {
    const b80 = zonas.find((z) => /b80new|b80\s*new/i.test(z.zona))
    if (b80?.idAcabadoUnidad && b80.idAcabadoUnidad > 0) {
      return b80.idAcabadoUnidad
    }
    const enc = zonas.find((z) => /encimera/i.test(z.zona))
    if (enc?.idAcabadoUnidad && enc.idAcabadoUnidad > 0) {
      return enc.idAcabadoUnidad
    }
  }
  const fuzzy = zonas.find(
    (z) =>
      z.zona === acabado.zona ||
      Boolean(acabado.descripcion && z.zona.includes(acabado.descripcion)) ||
      Boolean(acabado.descripcion && acabado.descripcion.includes(z.zona)),
  )
  if (fuzzy?.idAcabadoUnidad && fuzzy.idAcabadoUnidad > 0) {
    return fuzzy.idAcabadoUnidad
  }
  return acabado.idAcabadoUnidad
}
export function getZonesFromGLB(
  children: Node[],
  acabados: Acabados[],
  extraInfo?: { zonas: { idAcabadoUnidad: number; zona: string }[] },
) {
  if (!acabados || !children) return { zones: {}, textures: [] }
  const textures: Textures = []
  let index = 0
  const zones: Zone = {}
  children.forEach((child) => {
    child.traverse((node) => {
      if (node instanceof Group) {
        const { position, scale, rotation } = node
        let { name } = node
        if (typeof name === 'number') {
          name = String(name)
        }
        zones[name] = {
          name,
          scale,
          position,
          rotation,
          index: 0,
        }
      }
    })
  })
  children.forEach((child) => {
    child.traverse((node) => {
      if (node instanceof Mesh) {
        let { name: nodeName, position, scale, rotation } = node
        if (typeof nodeName === 'number') {
          nodeName = String(nodeName)
        }
        const { name: materialName } = node.material
        const acabado = findMatchingAcabado(materialName, acabados)
        if (!acabado) {
          // console.warn(`No acabado found for material: ${materialName}`)
          return
        }
        index++
        const materials = getStandardMaterial(acabado.texturas)
        const linkedTextureID = resolveLinkedTextureIdForAcabado(acabado, extraInfo)
        const textureId = `${nodeName}_${acabado.idAcabadoUnidad}`
        const zoneDisplayName = acabado.descripcion || acabado.zona
        textures.push({
          id: materials.id,
          searchID: materials.searchID,
          zoneName: nodeName,
          zone: acabado.zona,
          name: materials.name,
          url: materials.url,
          zoneDisplayName,
          groupID: materials.groupID,
          linkedTextureID: linkedTextureID,
          modelo: acabado.modelo || acabado.descripcion,
          acabado: acabado.acabado || acabado.descripcion,
          idAcabadoUnidadModeloAcabado: textureId,
          color: materials.color,
          roughness: materials.roughness,
          metalness: materials.metalness,
          envMapIntensity: materials.envMapIntensity,
          bumpScale: materials.bumpScale,
          opacity: materials.opacity,
          repeatX: materials.repeatX,
          repeatY: materials.repeatY,
          textureRotation: materials.textureRotation,
          mirror: materials.mirror,
        })
        const zoneMaterial = getStandardMaterial(
          acabado.texturas,
          acabado.modelo || acabado.descripcion,
          acabado.acabado || acabado.descripcion,
        )
        zones[nodeName] = {
          position,
          scale,
          rotation,
          name: nodeName,
          material: {
            ...zoneMaterial,
            zone: acabado.zona,
            zoneDisplayName,
            zoneName: nodeName,
          },
          index: index - 1,
          idAcabadoUnidadModeloAcabado: textureId,
        }
      }
    })
  })
  const validZonesFromAPI = collectValidZonesFromUnitAcabados(acabados)
  if (validZonesFromAPI.size > 0 && !validZonesFromAPI.has('Costado')) {
    const filteredTextures = textures.filter(
      (texture) => texture.zoneDisplayName !== 'Costado' && texture.zone !== 'Costado',
    )
    Object.keys(zones).forEach((zoneKey) => {
      const mat = zones[zoneKey]?.material as
        | { zoneDisplayName?: string; zone?: string }
        | undefined
      const isCostadoZone =
        zoneKey === 'Costado' || mat?.zoneDisplayName === 'Costado' || mat?.zone === 'Costado'
      if (!isCostadoZone) return
      if (mat && validZonesFromAPI.has('Puerta')) {
        mat.zoneDisplayName = 'Puerta'
        mat.zone = 'Puerta'
        return
      }
      delete zones[zoneKey]
    })
    return { zones, textures: filteredTextures }
  }
  return { zones, textures }
}
export function handleNodeTextures(
  node: Node & { name: keyof Zone },
  zones: Zone,
  textures: Textures,
  repeatX: number,
  repeatY: number,
  textureRotation: number,
  paginaPdf: number | undefined,
  itemHeight: number,
  itemWidth: number,
  itemThickness: number,
  getContiguousItems: (() => ContiguousAnalysisResult) | undefined,
  fatherId: string | undefined = undefined,
  universal: boolean,
  stableLinkedZoneTextures = false,
  freeDimensionTextureScale?: { x: number; y: number; z: number },
  preferredTexture?: Textures[number],
) {
  let newTexture
  if (textures.length > 0) {
    textures?.forEach((texture: Textures[number]) => {
      if (
        textureTargetsNode(texture, String(node.name), textures, zones) ||
        texture === preferredTexture
      ) {
        if (!texture.url) {
          applyZoneMaterialFromZones(node, zones, texture)
          return
        }
        newTexture = loadCachedTexture(texture.url, (baseTexture) => {
          try {
          const usedCadSwatchUvs = applyCatalogSwatchUvsIfNeeded(node, {
            panel:
              isLinkedPanelFinishTexture(texture) ||
              (texture.modelo === 'AcabadoModelo' &&
                isNumericCadZoneName(texture.zoneDisplayName || texture.zone)),
            force:
              texture.modelo === 'AcabadoModelo' &&
              isNumericCadZoneName(texture.zoneDisplayName || texture.zone),
          })
          const loadedTexture = baseTexture.clone()
          loadedTexture.wrapS = RepeatWrapping
          loadedTexture.wrapT = RepeatWrapping
          loadedTexture.flipY = false
          // console.log(texture.idAcabadoUnidadModeloAcabado, texture.zone, texture.zoneDisplayName)
          const isCountertop = texture.zone === 'Encimera' || texture.zoneDisplayName === 'Encimera'
          const isFrontal = isFrontalZoneTexture(texture)
          const isHorno =
            isNumericCadZoneName(texture.zone) || isNumericCadZoneName(texture.zoneDisplayName)
          const isSpecialCounterTop =
            isCountertop && texture.idAcabadoUnidadModeloAcabado === 'Box001_15054'
          const isSpecialCounterTop2 =
            isCountertop && texture.idAcabadoUnidadModeloAcabado === 'Box001_27761'
          let offsetU = 0
          let offsetV = 0

          // IMPORTANTE: Para objetos universales, siempre usar este bloque
          // y no aplicar las lógicas especiales de encimeras, frontales, etc.
          if (universal) {
            loadedTexture.offset.set(0, 0)
            loadedTexture.rotation = 0
            loadedTexture.repeat.set(1, 1)
            loadedTexture.wrapS = RepeatWrapping
            loadedTexture.wrapT = RepeatWrapping
            loadedTexture.flipY = false
            loadedTexture.needsUpdate = true
            // Universales (Item3D/Hole3D): asignación directa; ObjectMesh mantiene refs propios.
            // setMaterialMapUnique aquí liberaba texturas ajenas al caché y dejaba el mesh en blanco.
            node.material.map = loadedTexture
            node.material.needsUpdate = true
            node.material.userData.currentTexture = texture.url
            node.material.userData.currentVisualKey = getLinkedZoneVisualKey(texture)
            applyZoneMaterialFromZones(node, zones, texture)
            tuneMaterialForAlzadoTexturedView(
              node.material,
              zones?.[node.name]?.material?.roughness as number | undefined,
            )
            return
          } else if (isSpecialCounterTop2) {
            // console.log('ENTRA EN specialCounterTop2', itemHeight, itemWidth, itemThickness)
          } else if (isSpecialCounterTop && getContiguousItems && fatherId) {
            // console.log('entra en specialCounterTop con contiguos')
            const contiguousResult = analyzeContiguousFurniture(getContiguousItems)
            const group = contiguousResult?.groups.find((g) => g.some((i) => i.id === fatherId))

            const placement = getEncimeraUvPlacement(contiguousResult?.groups ?? [], fatherId)

            if (group && placement) {
              // Aquí la UV del mesh va en 0..1, así que además del offset hay que escalar el
              // trozo del atlas que ocupa el mueble. El sitio (U, V y giro) sale del mismo
              // cálculo que la encimera normal: fila desenrollada, o isla proyectada en planta.
              const repeatXCalculated = itemWidth / ENCIMERA_ATLAS_WIDTH_MM
              const repeatYCalculated = itemThickness / ENCIMERA_ATLAS_HEIGHT_MM

              offsetU = placement.offsetU
              offsetV = placement.offsetV

              // Configurar textura con offset y repeat
              loadedTexture.offset.set(offsetU, offsetV)
              loadedTexture.repeat.set(repeatXCalculated, repeatYCalculated)
              // La rotación del acabado sigue sin aplicarse aquí (calibrado de este GLB);
              // solo el giro que pide la isla, que en modo fila es 0.
              loadedTexture.rotation = radians(placement.extraRotationDeg)
              loadedTexture.wrapS = RepeatWrapping
              loadedTexture.wrapT = RepeatWrapping
              loadedTexture.flipY = false
              loadedTexture.needsUpdate = true

              assignNodeTexture(node, texture, zones, loadedTexture, baseTexture)
            }
          } else if (isSpecialCounterTop) {
            // console.log('entra en specialCounterTop')
            const FULL_WIDTH = 3200
            const FULL_HEIGHT = 1200
            const FULL_REPEAT_X = 1
            const FULL_REPEAT_Y = 1

            const repeatXCalculated = (itemWidth / FULL_WIDTH) * FULL_REPEAT_X
            const repeatYCalculated = (itemThickness / FULL_HEIGHT) * FULL_REPEAT_Y

            if (node.isMesh && node.geometry) {
              const geometry = node.geometry
              const uv = geometry.attributes.uv

              if (uv) {
                // Crear una copia de las UVs originales
                const newUvs = new Float32Array(uv.array.length)

                for (let i = 0; i < uv.count; i++) {
                  const index = i * 2
                  newUvs[index] = uv.array[index] // U se mantiene igual
                  newUvs[index + 1] = 1 - uv.array[index + 1] // V se invierte (1-v)
                }

                // Reemplazar las UVs
                geometry.setAttribute('uv', new THREE.BufferAttribute(newUvs, 2))
                geometry.attributes.uv.needsUpdate = true
              }
            }

            // Configurar la textura normalmente
            loadedTexture.repeat.set(repeatXCalculated, repeatYCalculated)
            loadedTexture.rotation = radians(textureRotation)
            loadedTexture.needsUpdate = true
            loadedTexture.flipY = false
            assignNodeTexture(node, texture, zones, loadedTexture, baseTexture)
            return
          } else if (isFrontal) {
            let geometry = node.isMesh ? node.geometry : undefined
            if (geometry?.attributes?.uv && !geometry.userData.__frontalUvFlipped) {
              if (!geometry.userData[GLB_INSTANCE_OWNED_GEOMETRY]) {
                node.geometry = isolateGeometryUserData(geometry.clone())
                node.geometry.userData[GLB_INSTANCE_OWNED_GEOMETRY] = true
                geometry = node.geometry
              } else {
                isolateGeometryUserData(geometry)
              }
              const uv = geometry.attributes.uv
              const newUvs = new Float32Array(uv.array.length)
              for (let i = 0; i < uv.count; i++) {
                const index = i * 2
                newUvs[index] = uv.array[index]
                newUvs[index + 1] = 1 - uv.array[index + 1]
              }
              geometry.setAttribute('uv', new THREE.BufferAttribute(newUvs, 2))
              geometry.attributes.uv.needsUpdate = true
              geometry.userData.__frontalUvFlipped = true
            }
            applyFrontalSlabTextureRepeat(
              loadedTexture,
              itemWidth,
              itemHeight,
              textureRotation,
            )
            assignNodeTexture(node, texture, zones, loadedTexture, baseTexture)
            return
          } else if (isCountertop && getContiguousItems && fatherId) {
            // Mantener la textura anterior para evitar el parpadeo negro
            const previousTexture = node.material.map
            const previousTextureUrl = node.material.userData.currentTexture as string | undefined
            const textureUrlKey = normalizeTextureUrl(texture.url)
            const previousUrlKey = previousTextureUrl
              ? normalizeTextureUrl(previousTextureUrl)
              : ''

            const contiguousResult = analyzeContiguousFurniture(getContiguousItems)
            const group = contiguousResult?.groups.find((g) => g.some((i) => i.id === fatherId))

            const placement = getEncimeraUvPlacement(contiguousResult?.groups ?? [], fatherId)

            if (group && group.length > 0 && placement) {
              // El sitio dentro del atlas (3200x1200mm) lo calcula getEncimeraUvPlacement:
              // en una fila suma anchos, y en una isla proyecta la posición real en planta
              // para que todas sus filas salgan de una sola plancha (offset también en V, y
              // giro de la veta según la orientación del mueble).
              const { offsetU, offsetV, extraRotationDeg } = placement

              // SIEMPRE reasignar un objeto de textura nuevo (clone) en vez de mutar el anterior
              // in-place. Mutar el MISMO objeto (previousTexture.offset.set(...)) no cambia su
              // referencia, así que R3F no re-vincula material-map y el `linkedMap` de React no se
              // entera: la nueva UV se calcula bien pero nunca se pinta, dejando la continuidad
              // "congelada" hasta remontar (togglear 2D/3D). Como el clone parte de baseTexture ya
              // cargada, la reasignación no produce parpadeo negro. `setMaterialMapUnique` (dentro de
              // assignNodeTexture) libera el clone anterior y retiene el base del caché.
              loadedTexture.rotation = radians(textureRotation + extraRotationDeg)
              loadedTexture.offset.set(offsetU, offsetV)
              loadedTexture.repeat.set(1, 1)
              loadedTexture.wrapS = RepeatWrapping
              loadedTexture.wrapT = RepeatWrapping
              loadedTexture.flipY = false
              loadedTexture.needsUpdate = true
              assignNodeTexture(node, texture, zones, loadedTexture, baseTexture)
            } else {
              // Si no hay grupo contiguo y hay textura anterior, mantenerla
              if (previousTexture && previousUrlKey === textureUrlKey) {
                // Mantener la textura anterior con sus parámetros actuales
                setMaterialMap(node.material, previousTexture)
              } else {
                assignNodeTexture(node, texture, zones, loadedTexture, baseTexture)
              }
            }
          } else if (paginaPdf === 1 || isHorno) {
            // console.log('ENTRA EN LA PÁGINAPDF 1', itemHeight, itemWidth, itemThickness)
            loadedTexture.offset.set(0, 0)
            loadedTexture.rotation = 0
            loadedTexture.repeat.set(1, 1)
            loadedTexture.needsUpdate = true
            assignNodeTexture(node, texture, zones, loadedTexture, baseTexture)
          } else {
            const stableFurnitureZone =
              usedCadSwatchUvs ||
              stableLinkedZoneTextures ||
              isLinkedFurnitureFinishTexture(texture)

            if (stableFurnitureZone) {
              loadedTexture.offset.set(0, 0)
              loadedTexture.rotation = radians(textureRotation)
              const scaleCompX = freeDimensionTextureScale?.x ?? 1
              const isZocaloZoneTex =
                texture.zoneDisplayName === 'Zocalo' ||
                texture.zone === 'Zocalo'
              const scaleCompZ =
                isZocaloZoneTex && freeDimensionTextureScale?.z
                  ? freeDimensionTextureScale.z
                  : 1
              loadedTexture.repeat.set(
                (repeatX ?? 1) * scaleCompX,
                (repeatY ?? 1) * scaleCompZ,
              )
              loadedTexture.wrapS = usedCadSwatchUvs
                ? ClampToEdgeWrapping
                : RepeatWrapping
              loadedTexture.wrapT = usedCadSwatchUvs
                ? ClampToEdgeWrapping
                : RepeatWrapping
              loadedTexture.flipY = false
              loadedTexture.needsUpdate = true
              assignNodeTexture(node, texture, zones, loadedTexture, baseTexture)
              return
            }

            // LÓGICA NORMAL - OFFSETS ALEATORIOS PARA OTRAS ZONAS
            offsetU = itemHeight > 1000 ? Math.random() - 0.5 : Math.random() * 2 - 1
            offsetV = 0
            loadedTexture.rotation =
              Math.random() > 0.5 ? radians(textureRotation) : radians(textureRotation + 180)
            loadedTexture.offset.set(offsetU, offsetV)
          }
          loadedTexture.needsUpdate = true
          assignNodeTexture(node, texture, zones, loadedTexture, baseTexture)
          } finally {
            scheduleInvalidateR3F()
          }
        })
      }
    })
  }
  return newTexture
}

/** Antes de exportar GLB para HD: asegurar emisión en meshes de zona Luz (tiras LED, etc.). */
export function ensureLuzZonesEmissiveForExport(
  scene: THREE.Object3D,
  luzMeshNames: Set<string> = new Set(),
) {
  scene.traverse((obj) => {
    if (!(obj instanceof Mesh) || !obj.material) return
    const meshName = String(obj.name)
    const materials = Array.isArray(obj.material) ? obj.material : [obj.material]
    materials.forEach((mat) => {
      if (mat.userData?.isLuzZone || luzMeshNames.has(meshName)) {
        mat.userData.isLuzZone = true
        applyLuzZoneEmissive({ material: mat } as Node)
      }
    })
  })
}

export function handleNodeTexturesVentanas(
  node: Node & { name: keyof Zone },
  zones: Zone,
  textures: Textures,
) {
  let newTexture
  if (textures.length > 0) {
    textures?.forEach((texture: Textures[number]) => {
      if (texture.zoneName === node.name) {
        newTexture = loader.load(texture.url + '?not-from-cache-please', (loadedTexture) => {
          clampTextureImageToGpuBudget(loadedTexture)
          loadedTexture.wrapS = RepeatWrapping
          loadedTexture.wrapT = RepeatWrapping
          loadedTexture.flipY = false
          loadedTexture.offset.set(0, 0)
          loadedTexture.rotation = 0
          loadedTexture.repeat.set(1, 1)
          loadedTexture.needsUpdate = true
          node.material.map = loadedTexture
          node.material.needsUpdate = true
          node.material.userData.currentTexture = texture.url
          node.material.userData.currentVisualKey = getLinkedZoneVisualKey(texture)
        })
      }
      return newTexture
    })
  }
}
